/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { enqueueTask } from 'ember-concurrency';
import moment from 'moment-timezone';
import {
  indexedEntities,
  TaskEvents,
  TaskResourceName,
  TaskResourceNameFromParentRecordResourceName
} from 'volta/models/task';
import TaskService from 'volta/services/task-service';
import { normalize } from 'volta/utils/api/serialize-and-push';
import { defaultErrorHandler } from 'volta/utils/error-utils';

import { action } from '@ember/object';
// eslint-disable-next-line ember/no-computed-properties-in-native-classes
import { sort } from '@ember/object/computed';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import type SessionUserService from 'volta/services/session-user';

import type { IntlService } from 'ember-intl';
import type StoreService from 'volta/services/store';
import type BvFlashService from 'volta/services/bv-flash';
import type EventStreamService from 'volta/services/event-stream';
import type { TTaskType, UTask } from 'volta/models/types/tasks';
import type { IResourceName } from 'volta/models/base-model';
import type Owner from '@ember/owner';
import type { TasksKitViewSelectSignature } from './view-select';
import type { ComponentLike, WithBoundArgs } from '@glint/template';
import type { TasksKitSubmitBtnSignature } from './submit-btn';
import type { TasksKitListEntitySignature } from 'volta/components/tasks-kit/list/entity';
import type { TasksKitListDateSignature } from './list/date';
import type { TasksKitFiltersSignature } from 'volta/components/tasks-kit/filters';
import type { TDate } from 'volta/models/types/calendar';
import { formatLocalDate, ISO_DATE_FORMAT, LOCAL_DATE_FORMAT } from "volta/utils/date-utils";

export type TSelectedView = 'open' | 'resolved';
export type TTaskListVariation = 'entity' | 'date-entity';

export interface TasksKitSignature {
  Args: {
    tasks: UTask[];
    resourceName?: IResourceName;
    scrollToDate?: TDate;
    groupingField?: 'dueDate' | 'createdAt';
    entityId: string;
    taskType?: TTaskType;
    isAutoResolvable?: boolean;
    listType?: TTaskListVariation;
    isLoading?: boolean;
    disabled?: boolean;
    authorizedUsers?: string[];
    onFilterChange?: (filters: TTaskType[]) => void;
    onDraftTask?: (task: UTask) => void;
    onSaveTask?: (task: UTask) => void;
    onDeleteTask?: (task: UTask) => void;
    onDestroy?: (task: UTask | undefined) => void;
  };
  Blocks: {
    default: [
      {
        ViewSelect: WithBoundArgs<
          ComponentLike<TasksKitViewSelectSignature>,
          'onViewChange' | 'resolvedCount' | 'selectedView' | 'unresolvedCount' | 'disabled'
        >;
        SubmitBtn: WithBoundArgs<
          ComponentLike<TasksKitSubmitBtnSignature>,
          'loading' | 'onClick' | 'disabled'
        >;
        List: ComponentLike<
          TasksKitListEntitySignature | TasksKitListDateSignature
        >;
        Filters: ComponentLike<TasksKitFiltersSignature>;
        initScroll: () => void;
        scrollToTask: () => void;
        selectedTask?: UTask;
        title: string;
        onDraft: (date?: string) => void;
      }
    ];
  };
}

export default class TasksKitComponent extends Component<TasksKitSignature> {
  @service intl!: IntlService;
  @service store!: StoreService;
  @service bvFlash!: BvFlashService;
  @service eventStream!: EventStreamService;
  @service taskService!: TaskService;
  @service sessionUser!: SessionUserService;

  resourceName = TaskResourceNameFromParentRecordResourceName(this.args.resourceName);
  taskResourceName = TaskResourceName;
  noDateId = this.intl.t('tasksPage.noDueDate');
  dueDateIdPrefix = 'task-';
  sortProperties = ['dueDate:asc', 'createdAt:asc'];

  @tracked selectedTask: UTask | undefined;
  @tracked selectedView: TSelectedView = 'open';
  @tracked taskFilters: string[] = [];

  @sort('filteredTasks', 'sortProperties')
  sortedTasks!: UTask[];

  get filteredTasks() {
    const isArchiveView = this.selectedView === 'resolved';
    const filteredTasks = (this.args.tasks ?? []).filter(
      (t) => this.filterFn(t) && (isArchiveView ? !!t.resolvedAt : !t.resolvedAt)
    );
    if (this.selectedTask) {
      filteredTasks.push(this.selectedTask);
    }

    return filteredTasks;
  }

  get resolvedTaskCount() {
    return (this.args.tasks ?? []).filter((t) => this.filterFn(t) && t.resolvedAt).length;
  }

  get unresolvedTaskCount() {
    return (this.args.tasks ?? []).filter((t) => this.filterFn(t) && !t.resolvedAt).length;
  }

  get indexedEntities() {
    return indexedEntities(this.args.tasks);
  }

  @cached
  get tasksTs(): Record<string, UTask[]> {
    if (this.args.listType === 'entity') {
      return TaskService.indexTasksByEntity(this.sortedTasks);
    }
    return TaskService.indexTasksByDate(this.sortedTasks, this.noDateId, this.groupingField);
  }

  get addTaskIsDisabled() {
    return this.args.tasks.some((t) => !t?.id) || !this.args.onDraftTask;
  }

  get groupingField() {
    return this.args.groupingField ?? 'createdAt';
  }

  constructor(owner: Owner, args: TasksKitSignature['Args']) {
    super(owner, args);
    this.addEventHandlers();
  }

  willDestroy() {
    super.willDestroy();
    this.eventStream.removeHandlers(this);
    this.args.onDestroy?.(this.selectedTask);
  }

  @action
  filterFn(t?: UTask) {
    return (
      t &&
      (this.taskFilters.length === 0 ||
        (this.taskFilters && this.taskFilters.includes(t.taskType))) &&
      t.id !== this.selectedTask?.id
    );
  }

  @action
  initScroll() {
    const { scrollToDate } = this.args;

    if (!this.selectedTask) {
      const formattedSelectedDate = scrollToDate ? formatLocalDate(scrollToDate) : undefined;

      if (
        !formattedSelectedDate ||
        (formattedSelectedDate && !this.tasksTs[formattedSelectedDate]) ||
        (this.args.isAutoResolvable &&
          (this.args.taskType === 'PLANNING_TASK' || this.args.taskType === 'EXECUTION_TASK'))
      ) {
        this.createNewTaskDraft(formattedSelectedDate);
      }
      this.scrollTo(formattedSelectedDate);
    }
  }

  @action
  scrollToTask() {
    if (!this.selectedTask) {
      return;
    }
    if (!this.selectedTask.id && !this.selectedTask[this.groupingField]) {
      this.scrollTo(this.noDateId);
    } else if (this.selectedTask.dueDate) {
      const formattedDueDate = formatLocalDate(this.selectedTask.dueDate);
      this.scrollTo(formattedDueDate);
    }
  }

  @action
  selectTask(task?: UTask, forceDelete = false): void {
    if (this.selectedTask && !task) {
      if (forceDelete && !this.selectedTask.id) {
        this.args.onDeleteTask?.(this.selectedTask);
      } else {
        this.handleSubmit.perform(this.selectedTask);
      }
    } else {
      if (this.selectedTask?.newWithoutComment) {
        this.args.onDeleteTask?.(this.selectedTask);
      }

      this.selectedTask = task;
    }
  }

  @action
  createNewTaskDraft(date?: string) {
    if (!this.resourceName) {
      return;
    }

    const newTask: UTask = this.store.createRecord(this.resourceName.singular, {
      linkedEntityId: this.args.entityId,
      createdAt: moment.utc().toDate(),
      dueDate:
        date && moment.utc(date, LOCAL_DATE_FORMAT).isValid() ? moment.utc(date, LOCAL_DATE_FORMAT).format(ISO_DATE_FORMAT) : undefined,
      taskType: this.args.taskType,
      resolveIf: 'MANUAL'
    }) as UTask;

    this.selectTask(newTask);
    this.scrollToTask();
    this.args.onDraftTask?.(newTask);
  }

  @action
  filterTasks(taskFilters: TTaskType[]) {
    this.taskFilters = taskFilters;
    this.args.onFilterChange?.(taskFilters);
  }

  handleSubmit = enqueueTask(async (task: UTask) => {
    try {
      // We delete remove the task if it has been added in the form
      // but not touched at all
      if (task.newWithoutComment) {
        return;
      }
      this.selectedTask = undefined;
      const saved = (await this.taskService.saveTask.perform(task)) as UTask;
      return this.args.onSaveTask?.(saved);
    } catch (error) {
      this._onError(error as string);
    }
  });

  handleResolve = enqueueTask(async (task: UTask) => {
    try {
      if (task.newWithoutComment) {
        return;
      }
      this.selectedTask = undefined;

      const resolvedTask = (
        task.resolvedAt
          ? await this.taskService.resolveTask.perform(task)
          : await this.taskService.reopenTask.perform(task)
      ) as UTask;
      return this.args.onSaveTask?.(resolvedTask);
    } catch (error) {
      this._onError(error as string);
    }
  });

  handleDelete = enqueueTask(async (task: UTask) => {
    if (!this.resourceName) {
      return;
    }
    try {
      await this.taskService.deleteTask.perform(task);
      this.selectedTask = undefined;
      this.args.onDeleteTask?.(task);
    } catch (error) {
      this._onError(error as string);
    }
  });

  private _onError(error: string): void {
    defaultErrorHandler(error);
    this.bvFlash.error(error);
  }

  private addEventHandlers() {
    if (this.args.resourceName) {
      this.eventStream.addHandler(
        this.args.resourceName.plural,
        TaskEvents.TaskCreated,
        this.onTaskCreated,
        this
      );
      this.eventStream.addHandler(
        this.args.resourceName.plural,
        TaskEvents.TaskUpdated,
        this.onTaskUpdated,
        this
      );
      this.eventStream.addHandler(
        this.args.resourceName.plural,
        TaskEvents.TaskDeleted,
        this.onTaskDeleted,
        this
      );
    }
  }

  private async onTaskCreated(payload: { [key: string]: any }) {
    await this.triggerOnEvent(payload, async (task: UTask) => {
      return this.args.onDraftTask?.(task);
    });
  }

  private async onTaskUpdated(payload: { [key: string]: any }) {
    await this.triggerOnEvent(payload, async (updatedTask: UTask) => {
      const task = this.args.tasks.find((t) => t.id === updatedTask.id);

      if (task) {
        task.comment = updatedTask.comment;
        task.dueDate = updatedTask.dueDate;
        task.resolvedAt = updatedTask.resolvedAt;
      }
    });
  }

  private async onTaskDeleted(payload: { [key: string]: any }) {
    await this.triggerOnEvent(payload, (task: UTask) => {
      return this.args.onDeleteTask?.(task);
    });
  }

  private async triggerOnEvent(payload: { [key: string]: any }, action: (task: UTask) => void) {
    const task = normalize('task', payload) as UTask;
    const { createdBy, taskType, linkedEntityId } = task;

    if (
      createdBy &&
      createdBy.id !== this.sessionUser.userId &&
      this.args.taskType === taskType &&
      linkedEntityId === this.args.entityId
    ) {
      action(task);
    }
  }

  private scrollTo(id?: string) {
    const elementToScrollTo = document.getElementById(`${this.dueDateIdPrefix}${id ?? 'New'}`);

    if (elementToScrollTo) {
      elementToScrollTo.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest'
      });
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    TasksKit: typeof TasksKitComponent;
    'tasks-kit': typeof TasksKitComponent;
  }
}
